import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { queries as homeQueries } from "../../redux/features/dashboards/homeDashboardSlice";
import { selectors as homeSelectors } from "../../redux/features/dashboards/homeDashboardSlice";
import { selectors as storeSelectors } from "../../redux/features/dashboards/storeDashboardSlice";
import { selectors as configurationSelectors } from "../../redux/features/dashboards/configurationDashboardSlice";
import { selectors as supplyChainSelectors } from "../../redux/features/dashboards/supplyChainDashboardSlice";
import { selectors as vendorSelectors } from "../../redux/features/dashboards/vendorDashboardSlice";
import { queries as vendorQueries } from "../../redux/features/dashboards/vendorDashboardSlice";
import { queries as storeQueries } from "../../redux/features/dashboards/storeDashboardSlice";
import { queries as configurationQueries } from "../../redux/features/dashboards/configurationDashboardSlice";
import { queries as supplyChainQueries } from "../../redux/features/dashboards/supplyChainDashboardSlice";
import "./TableComponent.scss";

export const TableComponent = ({
  metadata,
  data: initialData,
  alternatingRowColor = false,
  fitColumns = false,
  thresholdValues = [],
  panelId,
  workstream,
}) => {
  const [sortInfo, setSortInfo] = useState({});
  const [sortCount, setSortCount] = useState(0);
  const propertyRefs = useRef({});
  const [tooltips, setTooltips] = useState({});
  const dispatch = useDispatch();

  // Los datos actualizados por filtros vienen a través de las props
  const currentData = initialData || [];

  // Obtener los selectores según el workstream
  const getWorkStreamTabContent = () => {
    switch (workstream) {
      case "home":
        return homeSelectors.selectHomeDashboardTabContent;
      case "stores":
        return storeSelectors.selectStoreDashboardTabContent;
      case "configuration":
        return configurationSelectors.selectConfigurationDashboardTabContent;
      case "supplyChain":
        return supplyChainSelectors.selectSupplyChainDashboardTabContent;
      case "vendor":
        return vendorSelectors.selectVendorDashboardTabContent;
      default:
        return () => null; // Retorna una función que retorna null
    }
  };

  // Usar el selector apropiado
  const tabContent = useSelector(getWorkStreamTabContent());

  // Función para convertir sortInfo a orderBy string
  const convertSortInfoToOrderBy = (sortInfo) => {
    if (!sortInfo || Object.keys(sortInfo).length === 0) return "";

    const sortedColumns = Object.entries(sortInfo)
      .sort(([, a], [, b]) => a.displayedIndex - b.displayedIndex)
      .map(([columnName, { order }]) => `${columnName} ${order.toUpperCase()}`)
      .join(", ");

    return sortedColumns;
  };

  const handleSort = async (referencedColumnName) => {
    setSortInfo((prev) => {
      const current = prev[referencedColumnName];
      let newSortInfo;

      if (!current) {
        const newIndex = sortCount + 1;
        setSortCount(newIndex);
        newSortInfo = {
          ...prev,
          [referencedColumnName]: { order: "asc", displayedIndex: newIndex },
        };
      } else if (current.order === "asc") {
        newSortInfo = {
          ...prev,
          [referencedColumnName]: { ...current, order: "desc" },
        };
      } else if (current.order === "desc") {
        const newSort = { ...prev };
        delete newSort[referencedColumnName];

        const sortedColumns = Object.keys(newSort)
          .sort((a, b) => newSort[a].displayedIndex - newSort[b].displayedIndex)
          .map((key, i) => ({ key, index: i + 1 }));

        const updatedSort = {};
        sortedColumns.forEach(({ key, index }) => {
          updatedSort[key] = { ...newSort[key], displayedIndex: index };
        });

        setSortCount(Object.keys(updatedSort).length);
        newSortInfo = updatedSort;
      } else {
        newSortInfo = prev;
      }

      const orderBy = convertSortInfoToOrderBy(newSortInfo);

      switch (workstream) {
        case "home":
          dispatch(
            homeQueries.updatePanelDataWithSorting({ panelId, orderBy })
          );
          break;
        case "stores":
          dispatch(
            storeQueries.updatePanelDataWithSorting({ panelId, orderBy })
          );
          break;
        case "configuration":
          dispatch(
            configurationQueries.updatePanelDataWithSorting({
              panelId,
              orderBy,
            })
          );
          break;
        case "supplyChain":
          dispatch(
            supplyChainQueries.updatePanelDataWithSorting({ panelId, orderBy })
          );
          break;
        case "vendor":
          dispatch(
            vendorQueries.updatePanelDataWithSorting({ panelId, orderBy })
          );
          break;
        default:
          console.warn(`Workstream ${workstream} not supported for sorting`);
      }

      return newSortInfo;
    });
  };

  const getColumnWidth = (column) => {
    if (column.specificWidth) return column.specificWidth;

    if (fitColumns) {
      const totalRemFixed = metadata
        .filter((col) => col.specificWidth)
        .reduce((sum, col) => sum + parseFloat(col.specificWidth), 0);

      const remPerColumn =
        (100 - ((totalRemFixed / 16) * 100) / (window.innerWidth / 16)) /
        metadata.filter((col) => !col.specificWidth).length;

      return `${remPerColumn}rem`;
    }

    return "auto";
  };

  const formatNumber = (value, column) => {
    if (typeof value !== "number") return value;
    let valueFormat = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: column.decimalNumbers || 0,
      maximumFractionDigits: column.decimalNumbers || 0,
    }).format(value);
    //add Prefix or Suffix
    if (column.prefix || column.suffix) {
      return `${column.prefix || ""}${valueFormat}${column.suffix || ""}`;
    }
    return valueFormat;
  };

  const isValueNumericInThreshold = (value, threshold) => {
    if (threshold.dataType !== "Numeric") return false;

    const numericValue = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(numericValue)) return false;

    const initialValue = parseFloat(threshold.initialValue);
    const finalValue = parseFloat(threshold.finalValue);

    return numericValue >= initialValue && numericValue <= finalValue;
  };

  const getRowStyle = (row) => {
    for (const threshold of thresholdValues) {
      if (threshold.dataType === "Numeric" && threshold.backgroundColor) {
        const columnValue = row[threshold.columnName];
        if (isValueNumericInThreshold(columnValue, threshold)) {
          return { backgroundColor: threshold.backgroundColor };
        }
      }
      if (
        threshold.dataType === "String" &&
        threshold.fixedValue &&
        threshold.backgroundColor
      ) {
        const columnValue = row[threshold.columnName];
        if (columnValue === threshold.fixedValue) {
          return { backgroundColor: threshold.backgroundColor };
        }
      }
    }
    return {};
  };

  const getCellStyle = (row, columnName) => {
    const threshold = thresholdValues.find((t) => t.columnName === columnName);
    if (
      threshold &&
      threshold.dataType === "Numeric" &&
      threshold.foregroundColor
    ) {
      const columnValue = row[threshold.columnName];
      if (isValueNumericInThreshold(columnValue, threshold)) {
        return { color: threshold.foregroundColor };
      }
    }
    if (
      threshold &&
      threshold.dataType === "String" &&
      threshold.fixedValue &&
      threshold.foregroundColor
    ) {
      const columnValue = row[threshold.columnName];
      if (columnValue === threshold.fixedValue) {
        return { color: threshold.foregroundColor };
      }
    }
    return {};
  };

  useEffect(() => {
    const newTooltips = {};
    Object.entries(propertyRefs.current).forEach(([key, el]) => {
      if (el) {
        newTooltips[key] = el.scrollWidth > el.clientWidth;
      }
    });
    setTooltips(newTooltips);
  }, []);

  return (
    <div
      className="kodo-table"
      style={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableContainer
        sx={{
          flex: 1,
          height: "100%",
          maxHeight: "100%",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Table
          style={{
            tableLayout: fitColumns ? "fixed" : "auto",
            width: "calc(100% - 5px)",
          }}
        >
          <TableHead>
            <TableRow>
              {metadata.map((column) => {
                return (
                  <TableCell
                    key={column.referencedColumnName}
                    style={{ width: getColumnWidth(column) }}
                    onClick={() => handleSort(column.referencedColumnName)}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span className="kodo-table-header-label">
                        {column.label}
                      </span>
                      {sortInfo[column.referencedColumnName] ? (
                        <>
                          {sortInfo[column.referencedColumnName].order ===
                          "asc" ? (
                            <i className={`arrow-button icon-arrowup active`}>
                              <u>
                                {
                                  sortInfo[column.referencedColumnName]
                                    .displayedIndex
                                }
                              </u>
                            </i>
                          ) : (
                            <i className={`arrow-button icon-arrowdown active`}>
                              <u>
                                {
                                  sortInfo[column.referencedColumnName]
                                    .displayedIndex
                                }
                              </u>
                            </i>
                          )}
                        </>
                      ) : (
                        <i className={`arrow-button icon-hyphen`}></i>
                      )}
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {currentData.map((row, rowIndex) => {
              const rowStyle = getRowStyle(row);
              return (
                <TableRow
                  key={rowIndex}
                  className={
                    alternatingRowColor
                      ? rowIndex % 2 === 0
                        ? "row-even"
                        : "row-odd"
                      : ""
                  }
                  style={rowStyle}
                >
                  {metadata.map((column) => {
                    const cellStyle = getCellStyle(
                      row,
                      column.referencedColumnName
                    );
                    return (
                      <TableCell
                        key={column.referencedColumnName}
                        style={{ width: getColumnWidth(column) }}
                      >
                        <Tooltip
                          title={
                            tooltips[
                              `${column.id}-${column.referencedColumnName}`
                            ]
                              ? column.useNumberFormat
                                ? formatNumber(
                                    row[column.referencedColumnName],
                                    column
                                  )
                                : row[column.referencedColumnName]
                              : ""
                          }
                          arrow
                        >
                          <p
                            className={`kodo-table-cell-content ${
                              typeof row[column.referencedColumnName] ==
                              "number"
                                ? "aling-info-right"
                                : ""
                            }`}
                            style={{
                              width: getColumnWidth(column),
                              ...cellStyle,
                            }}
                            ref={(el) => {
                              if (el) {
                                propertyRefs.current[
                                  `${column.id}-${column.referencedColumnName}`
                                ] = el;
                              }
                            }}
                          >
                            {column.useNumberFormat
                              ? formatNumber(
                                  row[column.referencedColumnName],
                                  column
                                )
                              : row[column.referencedColumnName]}
                          </p>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
